import type { EnvironmentId, ScopedThreadRef, ThreadId } from "@afar-research/contracts";
import { useAtomValue } from "@effect/atom-react";
import { Atom } from "effect/unstable/reactivity";
import { AsyncResult } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { appAtomRegistry } from "../../rpc/atomRegistry";
import { useProjects, useServerConfigs, useThreadShell } from "../../state/entities";
import { threadEnvironment } from "../../state/threads";
import { useAtomCommand } from "../../state/use-atom-command";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  parseLinkPullRequestInput,
  projectPullRequestRepository,
} from "./linkPullRequestTarget.logic";
import { supportsThreadPullRequests } from "./threadPullRequestCapability";

/**
 * Which thread has the link dialog open, set by whichever entry point asked — the thread's own
 * pull requests panel, or the command palette. Held here rather than in the panel so the dialog
 * outlives a palette that closes the moment its command runs, and so a thread with no panel open
 * can still be linked to.
 */
const linkDialogThreadAtom = Atom.make<ScopedThreadRef | null>(null).pipe(
  Atom.keepAlive,
  Atom.withLabel("pull-requests:link-dialog-thread"),
);

export function openLinkPullRequestDialog(threadRef: ScopedThreadRef): void {
  appAtomRegistry.set(linkDialogThreadAtom, threadRef);
}

/** The way out, for a caller that opened the dialog and now needs it gone. */
export function closeLinkPullRequestDialog(): void {
  appAtomRegistry.set(linkDialogThreadAtom, null);
}

/**
 * Mounted once by the chat view; shows the dialog for whichever thread asked for it. The thread
 * is the only thing an entry point has to name — everything the dialog needs beyond it is read
 * from that thread here, so no caller has to assemble it.
 */
export function LinkPullRequestDialogHost() {
  const threadRef = useAtomValue(linkDialogThreadAtom);
  const thread = useThreadShell(threadRef);
  const projects = useProjects();
  const configs = useServerConfigs();
  const project = projects.find(
    (candidate) =>
      candidate.environmentId === threadRef?.environmentId && candidate.id === thread?.projectId,
  );
  const close = useCallback((open: boolean) => {
    if (!open) closeLinkPullRequestDialog();
  }, []);
  // The thread outlives this host in a keepAlive atom, so leaving the chat view must forget it.
  // Otherwise coming back — to a different thread — reopens the dialog still bound to the old one.
  useEffect(() => closeLinkPullRequestDialog, []);
  // Nothing has asked for the dialog, so there is nothing to show.
  if (threadRef == null) return null;
  // An Environment that cannot hold the link has nothing to show a dialog for.
  if (!supportsThreadPullRequests(configs, threadRef.environmentId)) return null;
  return (
    <LinkPullRequestDialog
      open
      environmentId={threadRef.environmentId}
      threadId={threadRef.threadId}
      expectedRevision={thread?.pullRequestsRevision ?? 0}
      projectRepository={projectPullRequestRepository(project?.repositoryIdentity)}
      onOpenChange={close}
    />
  );
}

/**
 * Links a pull request to a thread from a number or a URL. Linking returns to the collection
 * rather than opening the pull request, because the user asked for the relationship, not for a
 * detour into the work.
 */
export function LinkPullRequestDialog({
  open,
  environmentId,
  threadId,
  expectedRevision,
  projectRepository,
  onOpenChange,
}: {
  readonly open: boolean;
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly expectedRevision: number;
  /** The thread's own repository, against which a bare number resolves. */
  readonly projectRepository: { readonly host: string; readonly repository: string } | null;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [reference, setReference] = useState("");
  // Nothing is wrong until the reader has said something. Validation that fires on an untouched
  // field reads as a rejection of the dialog itself.
  const [dirty, setDirty] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const link = useAtomCommand(threadEnvironment.linkPullRequest, { reportFailure: false });
  const projects = useProjects();
  const parsed = useMemo(
    () => parseLinkPullRequestInput(reference, projectRepository),
    [projectRepository, reference],
  );
  // A URL is a locator, never a grant: this Environment reads a pull request with the
  // credentials of a Project checked out from that host, so a host it has none for is said to
  // be unreadable here instead of being linked as a membership nothing can ever fill in.
  const readableHost = useMemo(() => {
    if (parsed.kind !== "target") return true;
    return projects.some(
      (project) =>
        project.environmentId === environmentId &&
        projectPullRequestRepository(project.repositoryIdentity)?.host === parsed.target.host,
    );
  }, [environmentId, parsed, projects]);
  const problem =
    parsed.kind === "needs-repository"
      ? "This thread's project has no GitHub repository, so a number cannot be resolved. Paste the pull request URL instead."
      : parsed.kind === "unrecognized"
        ? "Enter a pull request number or a pull request URL."
        : readableHost
          ? null
          : `No project in this Environment is checked out from ${parsed.kind === "target" ? parsed.target.host : "that host"}, so it cannot read this pull request.`;
  const linkable = parsed.kind === "target" && readableHost;

  // Reopening starts clean: a reference left behind by a cancelled attempt is not what this
  // reader asked to link. Focus waits a frame so the dialog owns it before the field takes it.
  useEffect(() => {
    if (!open) return;
    setReference("");
    setDirty(false);
    setSubmitError(null);
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  const submit = useCallback(async () => {
    setDirty(true);
    if (!linkable || parsed.kind !== "target" || pending) return;
    setSubmitError(null);
    setPending(true);
    const result = await link({
      environmentId,
      input: {
        threadId,
        host: parsed.target.host,
        repository: parsed.target.repository,
        number: parsed.target.number,
        url: parsed.target.url,
        source: "manual",
        expectedRevision,
      },
    });
    setPending(false);
    // A refused link keeps the dialog and the reference: the reader is one correction away, and
    // closing would make them retype it to find out what went wrong.
    if (!AsyncResult.isSuccess(result)) {
      setSubmitError("Could not link this pull request. Check the reference and try again.");
      return;
    }
    setReference("");
    onOpenChange(false);
  }, [environmentId, expectedRevision, link, linkable, onOpenChange, parsed, pending, threadId]);

  return (
    <Dialog open={open} onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}>
      <DialogPopup className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Link Pull Request</DialogTitle>
          <DialogDescription>
            A number links a pull request in this thread&apos;s own repository. A URL may name
            another repository this AFAR Environment can read.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-3">
          <Input
            ref={inputRef}
            placeholder="#42 or https://github.com/owner/name/pull/42"
            value={reference}
            onChange={(event) => {
              setDirty(true);
              setReference(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              void submit();
            }}
          />
          {/* What the reference resolved to, before it is linked. A typo in an owner or a number
              is otherwise invisible until the link lands on the wrong pull request. */}
          {linkable && parsed.kind === "target" ? (
            <p className="truncate text-xs text-muted-foreground">
              {parsed.target.host}/{parsed.target.repository} #{parsed.target.number}
            </p>
          ) : null}
          {dirty && reference.trim().length > 0 && problem !== null ? (
            <p className="text-xs text-destructive">{problem}</p>
          ) : null}
          {submitError !== null ? <p className="text-xs text-destructive">{submitError}</p> : null}
        </DialogPanel>
        <DialogFooter>
          <Button
            disabled={pending}
            onClick={() => onOpenChange(false)}
            size="sm"
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            disabled={pending || !linkable}
            onClick={() => void submit()}
            size="sm"
            type="button"
          >
            {pending ? "Linking…" : "Link"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
